import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { CustomerAddModalFormSkeleton } from "./CustomerLoadingSkeletons";
import {
  peopleSelectControlSx,
  peopleSelectSx,
} from "../peopleSelectStyles";
import { customerEditSelectMenuProps } from "./customersFilterUiShared";
import {
  fieldErrorSx,
  hasValidationErrors,
  peopleFieldMaxLengthProps,
  peopleFormErrorTextSx,
  validateCustomerForm,
} from "../peopleFormValidation";

const fieldLabelSx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  mb: 0.75,
};

const inputSx = {
  width: 360,
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    background: "#FFF",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    fontFamily: "Inter",
    fontSize: 14,
    "& fieldset": { border: "1px solid #D5DCE1" },
    "&:hover fieldset": { border: "1px solid #D5DCE1" },
    "&.Mui-focused fieldset": { border: "1px solid #D5DCE1" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 1.5,
    height: 40,
    boxSizing: "border-box",
    "&::placeholder": { color: "#C5CED6", opacity: 1 },
  },
};

const selectSx = peopleSelectControlSx({ width: 176, flexShrink: 0 });

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  company: "",
  groupId: "",
};

export default function CustomerAddModal({
  open,
  onClose,
  onConfirm,
  groupOptions = [],
  optionsLoading = false,
  submitting = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({ name: "", email: "", phone: "" });

  const selectedGroupLabel = groupOptions.find((g) => g.id === form.groupId)?.label;

  const selectMenuProps = customerEditSelectMenuProps({ openUpward: true });

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setErrors({ name: "", email: "", phone: "" });
    }
  }, [open]);

  const set = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    setForm((prev) => ({ ...prev, groupId }));
    setErrors((prev) => ({ ...prev, groupId: "" }));
  };

  const handleSubmit = () => {
    const nextErrors = validateCustomerForm(form);
    if (hasValidationErrors(nextErrors)) {
      setErrors(nextErrors);
      return;
    }
    onConfirm({
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
    });
    setForm(EMPTY_FORM);
    setErrors({ name: "", email: "", phone: "" });
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setErrors({ name: "", email: "", phone: "" });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 408,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "18px",
            mb: 2.5,
          }}
        >
          Add Customer
        </Typography>

        {optionsLoading ? (
          <CustomerAddModalFormSkeleton />
        ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Name</Typography>
            <TextField
              fullWidth
              placeholder="Enter name"
              value={form.name}
              onChange={set("name")}
              error={Boolean(errors.name)}
              sx={{ ...inputSx, ...fieldErrorSx(errors.name) }}
              {...peopleFieldMaxLengthProps("name")}
            />
            {errors.name ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.name}</Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Email</Typography>
            <TextField
              fullWidth
              placeholder="Enter email address"
              value={form.email}
              onChange={set("email")}
              error={Boolean(errors.email)}
              sx={{ ...inputSx, ...fieldErrorSx(errors.email) }}
              {...peopleFieldMaxLengthProps("email")}
            />
            {errors.email ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.email}</Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Phone</Typography>
            <TextField
              fullWidth
              placeholder="Enter phone number"
              value={form.phone}
              onChange={set("phone")}
              error={Boolean(errors.phone)}
              sx={{ ...inputSx, ...fieldErrorSx(errors.phone) }}
              {...peopleFieldMaxLengthProps("phone")}
            />
            {errors.phone ? (
              <Typography sx={peopleFormErrorTextSx}>{errors.phone}</Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Company</Typography>
            <TextField
              fullWidth
              placeholder="Enter company name"
              value={form.company}
              onChange={set("company")}
              sx={inputSx}
              {...peopleFieldMaxLengthProps("company")}
            />
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Group</Typography>
            <FormControl size="small" sx={{ ...selectSx, width: "100%" }}>
              <Select
                displayEmpty
                value={form.groupId}
                onChange={handleGroupChange}
                IconComponent={Dropdown}
                MenuProps={selectMenuProps}
                sx={peopleSelectSx}
                renderValue={() => (
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      color: selectedGroupLabel ? "#061522" : "#C5CED6",
                      fontSize: 14,
                      fontFamily: "Inter",
                      fontWeight: 400,
                      lineHeight: 1.2,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {selectedGroupLabel || "Select"}
                  </Typography>
                )}
              >
                {groupOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              background: "#FFF",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#405261",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#FFF", borderColor: "#C5CED6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || optionsLoading}
            sx={{
              width: 176,
              height: 40,
              borderRadius: "8px",
              background: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              "&:hover": { background: "#0077CC" },
              "&.Mui-disabled": {
                color: "#93A1B0",
                bgcolor: "#F9FAFA",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {submitting ? "Adding…" : "Add Customer"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
